import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface JobArchiveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
  onArchived: () => void;
}

export function JobArchiveDialog({
  isOpen,
  onClose,
  jobId,
  jobTitle,
  onArchived,
}: JobArchiveDialogProps) {
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleArchive = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for archiving this job");
      return;
    }

    setIsLoading(true);
    try {
      // Get current user's profile
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      console.log("Archive process - User:", user.id, "Profile:", profile);

      if (!profile) throw new Error("Profile not found");

      // Insert via secure RPC to bypass RLS edge cases
      const { data, error } = await supabase.rpc('archive_job_for_current_user', {
        p_job_id: jobId,
        p_reason: reason.trim(),
      });

      console.log("Archive RPC result:", { data, error });

      if (error) throw error;

      toast.success("Job archived successfully");
      onArchived();
      onClose();
      setReason("");
    } catch (error: any) {
      console.error("Error archiving job:", error);
      if (error.message?.includes("violates row-level security policy")) {
        toast.error("Cannot archive this job - archiving is only allowed for unpaid jobs or cancelled jobs.");
      } else {
        toast.error("Failed to archive job: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md" aria-describedby="archive-description">
        <DialogHeader>
          <DialogTitle>Archive Job</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p id="archive-description" className="text-sm text-muted-foreground mb-2">
              You are about to archive: <strong>{jobTitle}</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              This will hide the job from your view. Please provide a reason:
            </p>
          </div>
          
          <div>
            <Label htmlFor="reason">Reason for archiving</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Job is not suitable for my skills, Location is too far, etc."
              rows={3}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setReason(""); onClose(); }} disabled={isLoading} aria-label="Cancel archiving job">
              Cancel
            </Button>
            <Button onClick={handleArchive} disabled={isLoading || !reason.trim()} aria-label="Confirm archive job">
              {isLoading ? "Archiving..." : "Archive Job"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}